import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { connect } from 'react-redux'
import {
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import styles from './Faq.module.scss'
import NewNavImproved from '../../components/Nav/NewNavImproved.tsx'
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx'
import useNavVisibility from '../../hooks/useNavVisibility.ts'
import { openTutorialModal } from '../../store/actions'

type DispatchProps = {
  openTutorialModal: () => void
}

type AnswerBlock =
  | { type: 'paragraph'; content: string }
  | { type: 'list'; items: string[] }

type FaqItem = {
  id: string
  question: string
  answer: AnswerBlock[]
}

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-right: 5%;
  padding-left: 5%;
  max-width: 150rem;
  margin: auto;
  @media (max-width: 1200px) {
    padding-right: 5%;
    padding-left: 5%;
  }
  @media (max-width: 900px) {
    padding-right: 0;
    padding-left: 0;
  }

  @media (max-width: 600px) {
    padding-right: 0;
    padding-left: 0;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  width: 100%;
  margin: 0 auto;
  padding: 2rem;


  @media (max-width: 900px) {
  padding-top: 8rem;
  padding-left: 2rem;
  padding-right: 2rem;
  }

  @media (max-width: 600px) {
    padding-top: 8rem;
    padding-left: 2rem;
    padding-right: 2rem;
  }

`

const TutorialNotice = styled.div`
  padding: 1.2rem 1.6rem;
  border-radius: 1.2rem;
  background: rgba(35, 75, 162, 0.08);
  color: #0f172a;
  font-size: 1.3rem;
  text-align: left;
`

const Faq: React.FC<DispatchProps> = ({ openTutorialModal }) => {
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility()
  const navigate = useNavigate()

  const faqItems = useMemo<FaqItem[]>(
    () => [
      {
        id: 'inicio',
        question: '¿Cómo empiezo a usar Edify?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Ingresá con tu cuenta, completá tu perfil y configurá las preferencias básicas. Con eso habilitás las herramientas principales para publicar proyectos, cotizar y seguir tus gestiones.'
          },
          {
            type: 'list',
            items: [
              'Actualizá tus datos de contacto para que otros usuarios puedan encontrarte.',
              'Elegí los rubros que te interesan para recibir recomendaciones acertadas.',
              'Explorá el panel principal donde vas a ver accesos a obras, materiales, marketplace y talento.'
            ]
          }
        ]
      },
      {
        id: 'obras',
        question: '¿Cómo publico una obra privada y recibo presupuestos?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Desde el panel de acciones seleccioná la opción de obras privadas, cargá el proyecto con su documentación y definí los rubros que necesitás. Cada rubro puede tener un plazo y condiciones propias.'
          },
          {
            type: 'list',
            items: [
              'Compartí planos, memorias descriptivas o planillas para mejorar la calidad de las cotizaciones.',
              'Elegí quiénes pueden acceder a la documentación y seguí las propuestas desde tus notificaciones.',
              'Confirmá qué proveedor contratás y cerrá la gestión directamente con él.'
            ]
          }
        ]
      },
      {
        id: 'cotizaciones-materiales',
        question: '¿Cómo solicito o respondo cotizaciones de materiales?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Podés crear una solicitud nueva con los materiales que necesitás o explorar cotizaciones abiertas para enviar tu propuesta. Todo el intercambio se gestiona dentro de la plataforma.'
          },
          {
            type: 'list',
            items: [
              'Publicá el detalle de materiales, plazos y adjuntá tu plantilla de costos.',
              'Recibí presupuestos en la bandeja de notificaciones y administralos por proyecto.',
              'Si sos proveedor, descargá la plantilla, completá tu oferta y subila nuevamente.'
            ]
          }
        ]
      },
      {
        id: 'talento',
        question: '¿Cómo encuentro profesionales o publico oportunidades laborales?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Edify conecta talento con empresas. Podés buscar perfiles, publicar ofertas o seguir tus postulaciones desde un único lugar.'
          },
          {
            type: 'list',
            items: [
              'Utilizá los filtros por rubro y ubicación para encontrar profesionales o trabajadores.',
              'Crea ofertas laborales detalladas para recibir postulaciones relevantes.',
              'Si sos candidato, revisá el estado de tus postulaciones y actualizá tu perfil con referencias.'
            ]
          }
        ]
      },
      {
        id: 'marketplace',
        question: '¿Qué puedo hacer en el Marketplace?',
        answer: [
          {
            type: 'paragraph',
            content:
              'El Marketplace te permite mostrar tus servicios, productos o documentación. Es un canal para generar contactos y nuevas oportunidades comerciales.'
          },
          {
            type: 'list',
            items: [
              'Cargá fichas de productos o servicios con fotos, descripciones y precios orientativos.',
              'Administrá tus publicaciones para mantenerlas actualizadas.',
              'Explorá la oferta disponible para encontrar aliados o complementar tus proyectos.'
            ]
          }
        ]
      },
      {
        id: 'documentacion',
        question: '¿Cómo gestiono o comercializo documentación técnica?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Podés organizar las documentaciones de tus proyectos y ofrecerlas de forma controlada a la comunidad. Cada entrega queda centralizada para que la compartas con quien corresponda.'
          },
          {
            type: 'list',
            items: [
              'Subí tus anteproyectos o paquetes de documentación y definí permisos por usuario.',
              'SeguÍ el interés generado para entender qué demanda tiene cada entrega.',
              'Mantené un histórico ordenado para reutilizar documentación en futuros negocios.'
            ]
          }
        ]
      },
      {
        id: 'tutorial',
        question: '¿Dónde accedo al tutorial interactivo?',
        answer: [
          {
            type: 'paragraph',
            content:
              'El tutorial Spotlight recorre las secciones esenciales de Edify. Podés iniciarlo en cualquier momento desde este panel presionando el botón “Ver tutorial interactivo”.'
          }
        ]
      }
    ],
    []
  )

  const [openItem, setOpenItem] = useState<string | null>(faqItems[0]?.id ?? null)
  const handleOpenTutorial = () => {
    navigate('/dashboard')
    window.setTimeout(() => {
      openTutorialModal()
    }, 250)
  }

  return (
    <Container>
      {!ocultarEnMobile && <NewNavImproved />}
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <Wrapper>
        <section className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Preguntas frecuentes</h1>
            <p className={styles.subtitle}>
              Reunimos los pasos principales para que gestiones obras, materiales, talento y marketplace dentro de Edify.
            </p>
          </div>
          {!ocultarEnMobile ? (
            <button type="button" className={styles.tutorialButton} onClick={handleOpenTutorial}>
              Ver tutorial interactivo
            </button>
          ) : (
            <TutorialNotice>
              El tutorial interactivo está disponible para pantallas grandes. Si necesitás recorrerlo, ingresá desde una
              computadora o tablet en modo horizontal.
            </TutorialNotice>
          )}
        </section>

        <section className={styles.accordion}>
          {faqItems.map((item) => {
            const isOpen = openItem === item.id
            return (
              <div key={item.id} className={styles.item}>
                <button
                  type="button"
                  className={styles.question}
                  aria-expanded={isOpen}
                  onClick={() => setOpenItem(isOpen ? null : item.id)}
                >
                  <span>{item.question}</span>
                  {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                <div className={`${styles.answer} ${isOpen ? styles.open : ''}`} aria-hidden={!isOpen}>
                  {item.answer.map((block, index) => {
                    if (block.type === 'paragraph') {
                      return (
                        <p key={`${item.id}-p-${index}`} className={styles.answerParagraph}>
                          {block.content}
                        </p>
                      )
                    }
                    return (
                      <ul key={`${item.id}-l-${index}`} className={styles.answerList}>
                        {block.items.map((text, idx) => (
                          <li key={`${item.id}-li-${idx}`}>{text}</li>
                        ))}
                      </ul>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </section>
      </Wrapper>
    </Container>
  )
}

const mapStateToProps = () => ({})

export default connect(mapStateToProps, {
  openTutorialModal
})(Faq)

